import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import PageHeader from "../../components/common/PageHeader";
import { filterPanelSx } from "../../components/common/filterStyles";
import SearchableMultiSelectFilter from "../../components/common/SearchableMultiSelectFilter";
import { useApi } from "../../hooks/useApi";
import { useBrandId } from "../../hooks/useBrandId";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert as MuiAlert,
  TextField,
  Menu,
  ListItemText,
  CircularProgress,
  Checkbox,
  Divider,
  Badge,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import {
  CdrnAlertStatus,
  ClientCdrnAlert,
  ClientCdrnAlertsResponse,
  CdrnMerchantFilterItem,
  CdrnDescriptorFilterItem,
  CdrnDeclineStatusCode,
  CdrnResolveStatusCode,
  formatVerifiReasonCode,
  CdrnPaymentTypeLabel,
} from "../../types/alerts";

// Simplified client-facing status filter
const ClientStatus = {
  ALL: 'ALL',
  PENDING: 'PENDING',
  RESOLVED: 'RESOLVED',
} as const;

type ClientStatus = typeof ClientStatus[keyof typeof ClientStatus];

const clientStatusToNative: Record<string, string[]> = {
  [ClientStatus.PENDING]: [CdrnAlertStatus.PENDING, CdrnAlertStatus.PROCESSING],
  [ClientStatus.RESOLVED]: [CdrnAlertStatus.RESOLVED, CdrnAlertStatus.DECLINED, CdrnAlertStatus.CANCELLED, CdrnAlertStatus.EXPORTING, CdrnAlertStatus.CLOSED],
};
import { formatCurrency } from "../../utils/formatCurrency";
import * as Sentry from "@sentry/react";
import { pascalCase } from "../../utils/textUtils";
import { HasPermission } from "../../functions/auth";
import { PERMISSIONS } from "../../constants/permissions";
import ClientCsvDialog from "../../components/clientAlerts/ClientCsvDialog";

// Column configuration
const COLUMNS_STORAGE_KEY = 'client_cdrn_alerts_visible_columns_v3';

interface ColumnConfig {
  id: string;
  label: string;
  isDefault: boolean;
}

const allColumns: ColumnConfig[] = [
  { id: 'status', label: 'Status', isDefault: true },
  { id: 'brand_name', label: 'Brand', isDefault: false },
  { id: 'alert_type', label: 'Type', isDefault: true },
  { id: 'descriptor', label: 'Descriptor', isDefault: true },
  { id: 'network_timestamp', label: 'Alert Timestamp', isDefault: true },
  { id: 'amount', label: 'Amount', isDefault: true },
  { id: 'card_number', label: 'Card Number', isDefault: true },
  { id: 'auth_code', label: 'Auth Code', isDefault: true },
  { id: 'arn', label: 'ARN', isDefault: false },
  { id: 'merchant', label: 'Merchant', isDefault: false },
  { id: 'reason_code', label: 'Reason Code', isDefault: false },
  { id: 'status_code', label: 'Resolution Code', isDefault: false },
  { id: 'transaction_date', label: 'Transaction Date', isDefault: false },
  { id: 'resolved_at', label: 'Resolved At', isDefault: false },
  { id: 'payment_type', label: 'Payment Type', isDefault: false },
  { id: 'cm_notes', label: 'Notes', isDefault: false },
  { id: 'matched_order_url', label: 'Order URL', isDefault: false },
  { id: 'matched_gateway_title', label: 'Matched Gateway', isDefault: false },
  { id: 'google_sheet_mid', label: 'Sheets MID', isDefault: false },
];

const defaultColumnIds = allColumns.filter(c => c.isDefault).map(c => c.id);
const validColumnIds = allColumns.map(c => c.id);

const getStoredColumns = (): string[] => {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter((id: string) => validColumnIds.includes(id));
      }
    }
  } catch (e) {
    console.error('Failed to parse stored columns', e);
  }
  return defaultColumnIds;
};

const saveStoredColumns = (columnIds: string[]) => {
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columnIds));
};

const formatDateTime = (dateString: string | Date | null | undefined) => {
  if (!dateString) return '--';
  const dateStr = dateString instanceof Date ? dateString.toISOString() : dateString;
  return dayjs.utc(dateStr).format('MMM DD, YYYY HH:mm');
};

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '--';
  return dayjs.utc(dateString).format('MMM DD, YYYY');
};

const formatCardNumber = (cardBin?: string | null, cardLast4?: string | null) => {
  if (!cardBin && !cardLast4) return '--';
  if (cardBin && cardLast4) {
    return `${cardBin}****${cardLast4}`;
  }
  if (cardLast4) {
    return `****${cardLast4}`;
  }
  return cardBin || '--';
};

const getClientStatusLabel = (nativeStatus: string): string => {
  const upper = nativeStatus.toUpperCase();
  if (upper === CdrnAlertStatus.PENDING || upper === CdrnAlertStatus.PROCESSING) {
    return 'Pending';
  }
  return 'Resolved';
};

interface RecordStatusColor {
  main: string;
  bg: string;
}

const clientStatusColors: Record<string, RecordStatusColor> = {
  Pending: { main: '#F4C67A', bg: '#FFF8E5' },
  Resolved: { main: '#3B82F6', bg: '#EFF6FF' },
};

const StatusChip = ({ status }: { status: string }) => {
  const label = getClientStatusLabel(status);
  const colors = clientStatusColors[label] || { main: '#6b7280', bg: '#f3f4f6' };
  return <Chip
    label={label}
    sx={{
      backgroundColor: colors.bg,
      color: colors.main,
      fontWeight: 500,
      borderRadius: '10px',
    }}
  />
};

// Status code labels for display
const resolveStatusCodeLabels: Record<string, string> = {
  '100': 'Credit & Cancellation processed',
  '101': 'Partial Credit & Cancellation processed',
  '102': 'Authorization Cancelled',
  '951': 'Transaction Previously credited, no balance remaining',
};

const declineStatusCodeLabels: Record<string, string> = {
  '900': 'Unmatched Case - General/Other',
  '901': 'Unmatched Case - Merchant not Participating',
  '902': 'Unmatched Case - Unable to locate Original Transaction',
  '940': 'Duplicate Request',
  '950': 'Merchant account closed, unable to process Credit',
  '952': 'Transaction Previously received a Chargeback, no balance remaining',
  '953': 'Request is outside of eligibility timeframe',
  '954': 'Transaction 3D Secure authenticated (Verified by Visa/MC SecureCode)',
  '955': 'Could not honor cancel request',
  '956': 'Matched, unable to stop order fulfillment',
  '957': 'Matched, Proceed with Chargeback for resolution',
};

const cancelStatusCodeLabels: Record<string, string> = {
  '130': 'Cancellation processed',
};

const formatStatusCode = (statusCode: string): string => {
  const description = resolveStatusCodeLabels[statusCode] || declineStatusCodeLabels[statusCode] || cancelStatusCodeLabels[statusCode];
  return description ? `${description} (${statusCode})` : statusCode;
};

const getResolveStatusCodeOptions = () => {
  return Object.entries(CdrnResolveStatusCode).map(([key, value]) => ({
    value,
    label: `${value} - ${resolveStatusCodeLabels[value] ?? pascalCase(key)}`,
  }));
};

const getDeclineStatusCodeOptions = () => {
  return Object.entries(CdrnDeclineStatusCode).map(([key, value]) => ({
    value,
    label: `${value} - ${declineStatusCodeLabels[value] ?? pascalCase(key)}`,
  }));
};

const canResolve = (status: string): boolean => {
  return status === CdrnAlertStatus.PENDING || status === CdrnAlertStatus.PROCESSING;
};

function CdrnAlertRow({
  alert, onResolve, visibleColumns,
}: {
  alert: ClientCdrnAlert;
  onResolve: (success: boolean, message?: string) => void;
  visibleColumns: string[];
}) {
  const { post, patch, loading, error } = useApi();
  const [open, setOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveStatus, setResolveStatus] = useState<string>('');
  const [statusCode, setStatusCode] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [activityDate, setActivityDate] = useState<Dayjs | null>(dayjs());
  const [editNotesDialogOpen, setEditNotesDialogOpen] = useState(false);
  const [editNotes, setEditNotes] = useState<string>('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenResolveDialog = () => {
    handleCloseMenu();
    setResolveDialogOpen(true);
    setResolveStatus('');
    setStatusCode('');
    setNotes('');
    setActivityDate(dayjs());
  };

  const handleCloseResolveDialog = () => {
    const shouldRefresh = !!error;
    setResolveDialogOpen(false);
    setResolveStatus('');
    setStatusCode('');
    setNotes('');
    setActivityDate(dayjs());
    if (shouldRefresh) {
      onResolve(false);
    }
  };

  const handleResolveDialogClose = (_event: unknown, reason: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    handleCloseResolveDialog();
  };

  const handleOpenEditNotesDialog = () => {
    handleCloseMenu();
    setEditNotesDialogOpen(true);
    setEditNotes(alert.cm_notes || '');
  };

  const handleCloseEditNotesDialog = () => {
    const shouldRefresh = !!error;
    setEditNotesDialogOpen(false);
    setEditNotes('');
    if (shouldRefresh) {
      onResolve(false);
    }
  };

  const handleEditNotesDialogClose = (_event: unknown, reason: string) => {
    if (reason === 'backdropClick') return;
    handleCloseEditNotesDialog();
  };

  const handleEditNotesSubmit = async () => {
    if (editNotes.trim() && !loading) {
      try {
        await patch(`/api/client-alerts/cdrn/notes`, {
          alertId: alert.verifi_cdrn_alert_id,
          notes: editNotes.trim(),
        });
        handleCloseEditNotesDialog();
        onResolve(true);
      } catch (err) {
        console.error('Failed to edit notes:', err);
      }
    }
  };

  const isCancelAlert = alert.alert_type === 'CANCEL';

  const handleResolveSubmit = async () => {
    const isValid = isCancelAlert
      ? resolveStatus && !loading
      : resolveStatus && statusCode && !loading;

    if (isValid) {
      try {
        await post(`/api/client-alerts/cdrn/resolve`, {
          status: resolveStatus,
          ...(!isCancelAlert && { statusCode }),
          alert: {
            ...alert,
            alert_id: alert.verifi_cdrn_alert_id,
            logs: null,
          },
          notes: notes || undefined,
          ...(activityDate && { activityDate: activityDate.format('YYYY-MM-DD') }),
        });
        handleCloseResolveDialog();
        onResolve(true);
      } catch (err) {
        console.error('Failed to resolve CDRN alert:', err);
      }
    }
  };

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'status':
        return (
          <TableCell key={columnId}>
            <StatusChip status={alert.status} />
          </TableCell>
        );
      case 'brand_name':
        return <TableCell key={columnId}>{alert.brand_name || '--'}</TableCell>;
      case 'alert_type':
        return <TableCell key={columnId}>
          {alert.alert_type === 'CANCEL' ? (
            <Chip
              label={pascalCase(alert.alert_type)}
              sx={{
                backgroundColor: '#FFF8E5',
                color: '#F4C67A',
                fontWeight: 500,
                borderRadius: '10px',
              }}
            />
          ) : pascalCase(alert.alert_type)}
        </TableCell>;
      case 'descriptor':
        return <TableCell key={columnId}>{alert.descriptor_name || '--'}</TableCell>;
      case 'network_timestamp':
        return <TableCell key={columnId}>{formatDateTime(alert.network_timestamp)}</TableCell>;
      case 'amount':
        return (
          <TableCell key={columnId}>
            {alert.reference_transaction_amount
              ? formatCurrency(alert.reference_transaction_amount, alert.reference_transaction_currency || 'USD', true)
              : '--'}
          </TableCell>
        );
      case 'card_number':
        return <TableCell key={columnId}>{formatCardNumber(alert.card_bin, alert.card_last4)}</TableCell>;
      case 'auth_code':
        return <TableCell key={columnId}>{alert.auth_code || '--'}</TableCell>;
      case 'arn':
        return <TableCell key={columnId}>{alert.arn || '--'}</TableCell>;
      case 'merchant':
        return <TableCell key={columnId}>{alert.merchant_name || alert.verifi_merchant_key || '--'}</TableCell>;
      case 'status_code':
        return <TableCell key={columnId}>{alert.status_code || '--'}</TableCell>;
      case 'reason_code':
        return <TableCell key={columnId}>{alert.reason_code || '--'}</TableCell>;
      case 'transaction_date':
        return <TableCell key={columnId}>{formatDate(alert.reference_transaction_date)}</TableCell>;
      case 'resolved_at':
        return <TableCell key={columnId}>{formatDateTime(alert.resolved_at)}</TableCell>;
      case 'payment_type':
        return <TableCell key={columnId}>{alert.payment_type ? (CdrnPaymentTypeLabel[alert.payment_type] || alert.payment_type) : '--'}</TableCell>;
      case 'cm_notes':
        return <TableCell key={columnId}>{alert.cm_notes || '--'}</TableCell>;
      case 'matched_order_url':
        return (
          <TableCell key={columnId}>
            {alert.matched_order_url ? (
              <a href={alert.matched_order_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#465fff' }}>
                Open <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
              </a>
            ) : '--'}
          </TableCell>
        );
      case 'matched_gateway_title':
        return <TableCell key={columnId}>{alert.matched_gateway_title || '--'}</TableCell>;
      case 'google_sheet_mid':
        return <TableCell key={columnId}>{alert.google_sheet_mid || '--'}</TableCell>;
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <TableRow key={alert.verifi_cdrn_alert_id} hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 48, padding: '4px !important', textAlign: 'center', verticalAlign: 'middle' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ padding: '4px', margin: '0 auto', display: 'flex' }}
          >
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        {allColumns.filter(c => visibleColumns.includes(c.id)).map(c => renderCell(c.id))}
        <TableCell>
          {(canResolve(alert.status) || alert.cm_notes) && (
            <>
              <IconButton
                aria-label="more actions"
                aria-controls={menuOpen ? `alert-menu-${alert.verifi_cdrn_alert_id}` : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                onClick={handleOpenMenu}
                size="small"
                sx={{ padding: '4px' }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                id={`alert-menu-${alert.verifi_cdrn_alert_id}`}
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {canResolve(alert.status) && (
                  <MenuItem onClick={handleOpenResolveDialog}>
                    <ListItemText>Resolve</ListItemText>
                  </MenuItem>
                )}
                {alert.cm_notes && (
                  <MenuItem onClick={handleOpenEditNotesDialog}>
                    <ListItemText>Edit Notes</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </TableCell>
      </TableRow>
      {/* Edit Notes Dialog */}
      <Dialog
        open={editNotesDialogOpen}
        onClose={handleEditNotesDialogClose}
        aria-labelledby="edit-notes-dialog-title"
      >
        <DialogTitle id="edit-notes-dialog-title">
          Edit Notes — Alert #{alert.alert_key}
        </DialogTitle>
        <DialogContent>
          {error && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {error}
            </MuiAlert>
          )}
          <TextField
            size="small"
            fullWidth
            label="Notes"
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            multiline
            minRows={3}
            maxRows={6}
            disabled={loading}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEditNotesDialog} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditNotesSubmit}
            variant="contained"
            color="primary"
            disabled={!editNotes.trim() || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={handleResolveDialogClose}
        aria-labelledby="resolve-alert-dialog-title"
        aria-describedby="resolve-alert-dialog-description"
      >
        <DialogTitle id="resolve-alert-dialog-title">
          Resolve Alert #{alert.alert_key}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="resolve-alert-dialog-description" sx={{ mb: 3 }}>
            Select the resolve status for this CDRN alert.
          </DialogContentText>
          {error && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {error}
            </MuiAlert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: isCancelAlert ? '1fr' : 'repeat(2, 1fr)' }, gap: 2 }}>
              <FormControl size="small" fullWidth required disabled={loading}>
                <InputLabel id="resolve-status-label">Resolve Status</InputLabel>
                <Select
                  labelId="resolve-status-label"
                  id="resolve-status"
                  value={resolveStatus}
                  label="Resolve Status"
                  onChange={(e) => {
                    setResolveStatus(e.target.value);
                    setStatusCode('');
                  }}
                >
                  {isCancelAlert && <MenuItem value="CANCELLED">Cancelled</MenuItem>}
                  {!isCancelAlert && <MenuItem value="RESOLVED">Resolved (Refunded)</MenuItem>}
                  <MenuItem value="DECLINED">Declined</MenuItem>
                </Select>
              </FormControl>
              {!isCancelAlert && (
                <FormControl size="small" fullWidth required disabled={loading || !resolveStatus}>
                  <InputLabel id="status-code-label">Resolution Code</InputLabel>
                  <Select
                    labelId="status-code-label"
                    id="status-code"
                    value={statusCode}
                    label="Resolution Code"
                    onChange={(e) => setStatusCode(e.target.value)}
                  >
                    {(resolveStatus === 'RESOLVED' ? getResolveStatusCodeOptions() : getDeclineStatusCodeOptions()).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Credit Date (Refund Date)"
                  value={activityDate}
                  onChange={(newValue) => setActivityDate(newValue)}
                  format="YYYY-MM-DD"
                  disabled={loading}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                    field: { clearable: true },
                  }}
                />
              </LocalizationProvider>
            </Box>
            <TextField
              size="small"
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              maxRows={4}
              disabled={loading}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseResolveDialog} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleResolveSubmit}
            variant="contained"
            color="primary"
            autoFocus
            disabled={!resolveStatus || (!isCancelAlert && !statusCode) || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Resolving...' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={visibleColumns.length + 2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Alert Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getClientStatusLabel(alert.status)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Alert Type
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {pascalCase(alert.alert_type)}
                  </Typography>
                </Box>
                {alert.reason_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Alert Reason
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatVerifiReasonCode(alert.reason_code, alert.payment_type)}
                    </Typography>
                  </Box>
                )}
                {alert.status_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Alert Resolution
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatStatusCode(alert.status_code)}
                    </Typography>
                  </Box>
                )}
                {alert.payment_type && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {CdrnPaymentTypeLabel[alert.payment_type] || alert.payment_type}
                    </Typography>
                  </Box>
                )}
                {alert.merchant_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Merchant
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.merchant_name}
                    </Typography>
                  </Box>
                )}
                {alert.descriptor_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Descriptor
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.descriptor_name}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {alert.reference_transaction_amount
                      ? formatCurrency(alert.reference_transaction_amount, alert.reference_transaction_currency || 'USD', true)
                      : '--'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Card Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCardNumber(alert.card_bin, alert.card_last4)}
                  </Typography>
                </Box>
                {alert.auth_code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Auth Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.auth_code}
                    </Typography>
                  </Box>
                )}
                {alert.arn && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      ARN
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.arn}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Transaction Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(alert.reference_transaction_date)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Alert Timestamp
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDateTime(alert.network_timestamp)}
                  </Typography>
                </Box>
                {alert.resolved_at && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Resolved At
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTime(alert.resolved_at)}
                    </Typography>
                  </Box>
                )}
                {alert.matched_order_url && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Order URL
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      <a href={alert.matched_order_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#465fff' }}>
                        {alert.matched_order_url} <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                      </a>
                    </Typography>
                  </Box>
                )}
                {alert.matched_gateway_title && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Matched Gateway
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.matched_gateway_title}
                    </Typography>
                  </Box>
                )}
                {alert.google_sheet_mid && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Sheets MID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {alert.google_sheet_mid}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function ClientCdrn() {
  const { brandId } = useBrandId();
  const { get, loading, error: apiError } = useApi();
  const [cdrnDescriptors, setCdrnDescriptors] = useState<CdrnDescriptorFilterItem[]>([]);
  const [cdrnMerchants, setCdrnMerchants] = useState<CdrnMerchantFilterItem[]>([]);
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // Read initial values from URL or use defaults
  const initialStartDate = urlSearchParams.get("startDate") ? dayjs.utc(urlSearchParams.get("startDate")) : dayjs.utc().subtract(7, 'day');
  const initialEndDate = urlSearchParams.get("endDate") ? dayjs.utc(urlSearchParams.get("endDate")) : dayjs.utc();
  const initialStatus = urlSearchParams.get("status") || 'ALL';
  const initialDescriptorIds = (urlSearchParams.get("descriptorIds") || '').split(',').filter(Boolean);
  const initialMerchantIds = (urlSearchParams.get("merchantIds") || '').split(',').filter(Boolean);
  const initialPage = urlSearchParams.get("page") ? Math.max(0, parseInt(urlSearchParams.get("page")!, 10)) : 0;
  const initialRowsPerPage = urlSearchParams.get("rowsPerPage") ? parseInt(urlSearchParams.get("rowsPerPage")!, 10) : 25;
  const initialSortBy = urlSearchParams.get("sortBy") || 'network_timestamp';
  const initialSortOrder = (urlSearchParams.get("sortOrder") === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc';

  const [startDate, setStartDate] = useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate);
  const [status, setStatus] = useState<string>(initialStatus);
  const [descriptorIds, setDescriptorIds] = useState<string[]>(initialDescriptorIds);
  const [merchantIds, setMerchantIds] = useState<string[]>(initialMerchantIds);
  const [alertsData, setAlertsData] = useState<ClientCdrnAlertsResponse | null>(null);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
  const [pageError, setPageError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // CSV export
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);

  // Column configuration
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const columnMenuOpen = Boolean(columnMenuAnchor);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  // Filter row collapse
  const hasActiveFilters = merchantIds.length > 0 || descriptorIds.length > 0;
  const [filtersExpanded, setFiltersExpanded] = useState(hasActiveFilters);
  const activeFiltersCount = (merchantIds.length > 0 ? 1 : 0) + (descriptorIds.length > 0 ? 1 : 0);

  const merchantOptions = cdrnMerchants.map((merchant) => ({ value: String(merchant.merchant_id), label: merchant.merchant_name }));
  const descriptorOptions = cdrnDescriptors.map((desc) => ({ value: String(desc.descriptor_id), label: desc.descriptor_name }));

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns(prev => {
      const newColumns = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      saveStoredColumns(newColumns);
      return newColumns;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(defaultColumnIds);
    saveStoredColumns(defaultColumnIds);
  };

  const fetchCdrnDescriptors = useCallback(async () => {
    try {
      const response = await get<{ descriptors: CdrnDescriptorFilterItem[] }>(`/api/client-alerts/cdrn/descriptors`);
      setCdrnDescriptors(response.descriptors);
    } catch (err) {
      console.error('Failed to fetch CDRN descriptors:', err);
      Sentry.captureException(err);
    }
  }, [get]);

  const fetchCdrnMerchants = useCallback(async () => {
    try {
      const response = await get<{ merchants: CdrnMerchantFilterItem[] }>(`/api/client-alerts/cdrn/merchants`);
      setCdrnMerchants(response.merchants);
    } catch (err) {
      console.error('Failed to fetch CDRN merchants:', err);
      Sentry.captureException(err);
    }
  }, [get]);

  useEffect(() => {
    fetchCdrnDescriptors();
  }, [fetchCdrnDescriptors]);

  useEffect(() => {
    fetchCdrnMerchants();
  }, [fetchCdrnMerchants]);

  // Update URL whenever search parameters change
  useEffect(() => {
    const params = new URLSearchParams(urlSearchParams);

    if (startDate) {
      params.set("startDate", startDate.format("YYYY-MM-DD"));
    } else {
      params.delete("startDate");
    }

    if (endDate) {
      params.set("endDate", endDate.format("YYYY-MM-DD"));
    } else {
      params.delete("endDate");
    }

    if (status && status !== ClientStatus.ALL) {
      params.set("status", status);
    } else {
      params.delete("status");
    }

    if (descriptorIds.length > 0) {
      params.set("descriptorIds", descriptorIds.join(','));
    } else {
      params.delete("descriptorIds");
    }

    if (merchantIds.length > 0) {
      params.set("merchantIds", merchantIds.join(','));
    } else {
      params.delete("merchantIds");
    }

    if (page > 0) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }

    if (rowsPerPage > 0) {
      params.set("rowsPerPage", rowsPerPage.toString());
    } else {
      params.delete("rowsPerPage");
    }

    if (sortBy !== 'network_timestamp') {
      params.set("sortBy", sortBy);
    } else {
      params.delete("sortBy");
    }

    if (sortOrder !== 'asc') {
      params.set("sortOrder", sortOrder);
    } else {
      params.delete("sortOrder");
    }

    setUrlSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, status, descriptorIds, merchantIds, page, rowsPerPage, sortBy, sortOrder]);

  const fetchAlerts = useCallback(async () => {
    if (!startDate || !endDate) {
      return;
    }

    setPageError(null);

    try {
      const params = new URLSearchParams({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        ...(status && status !== ClientStatus.ALL && clientStatusToNative[status] && { statuses: clientStatusToNative[status].join(',') }),
        ...(descriptorIds.length > 0 && { descriptorIds: descriptorIds.join(',') }),
        ...(merchantIds.length > 0 && { merchantIds: merchantIds.join(',') }),
        sortBy,
        sortOrder,
        ...(brandId && { brandId: String(brandId) }),
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
      });

      const response = await get<ClientCdrnAlertsResponse>(`/api/client-alerts/cdrn?${params.toString()}`);
      setAlertsData(response);
    } catch (err) {
      console.error('Failed to fetch CDRN alerts:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
      setAlertsData(null);
    }
  }, [brandId, get, startDate, endDate, status, descriptorIds, merchantIds, sortBy, sortOrder, page, rowsPerPage]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
    setPage(0);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
    setPage(0);
  };

  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    setStatus(e.target.value);
    setPage(0);
  };

  const handleChangePage = async (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleResolve = useCallback(async (success: boolean = true, message?: string) => {
    await fetchAlerts();
    if (success) {
      setSnackbar({
        open: true,
        message: message || 'Alert resolved successfully',
        severity: 'success',
      });
    } else if (message) {
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    }
  }, [fetchAlerts]);

  return (
    <div>
      <PageMeta
        title="Chargemont | CDRN Alerts"
        description="Chargemont | CDRN Alerts"
      />
      <PageContainer>
        <PageHeader
          title="CDRN Alerts"
          action={
            <HasPermission permission={PERMISSIONS.ALERTS_OWN}>
              <Button
                variant="contained"
                onClick={() => setCsvDialogOpen(true)}
                sx={{
                  backgroundColor: '#465fff',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#3641f5',
                  },
                }}
              >
                Export CSV
              </Button>
            </HasPermission>
          }
        />

        {/* Filters */}
        <Box
          className="mb-6"
          sx={filterPanelSx}
        >
          {/* First Row: Date Pickers + Status + Filter Toggle */}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DatePicker
                    label="Start"
                    value={startDate}
                    onChange={handleStartDateChange}
                    open={startPickerOpen}
                    onOpen={() => setStartPickerOpen(true)}
                    onClose={() => setStartPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "fit-content",
                        },
                        onClick: () => setStartPickerOpen(true),
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    —
                  </Typography>
                  <DatePicker
                    label="End"
                    value={endDate}
                    onChange={handleEndDateChange}
                    open={endPickerOpen}
                    onOpen={() => setEndPickerOpen(true)}
                    onClose={() => setEndPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "fit-content",
                        },
                        onClick: () => setEndPickerOpen(true),
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>

            {/* Right side: Status + Filter Toggle */}
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  value={status}
                  label="Status"
                  onChange={handleStatusChange}
                >
                  <MenuItem value={ClientStatus.ALL}>All</MenuItem>
                  <MenuItem value={ClientStatus.PENDING}>Pending</MenuItem>
                  <MenuItem value={ClientStatus.RESOLVED}>Resolved</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: filtersExpanded ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: filtersExpanded ? 'primary.50' : 'transparent',
                  '&:hover': {
                    backgroundColor: filtersExpanded ? 'primary.100' : 'action.hover',
                  },
                }}
              >
                <Badge
                  badgeContent={activeFiltersCount}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: 10,
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  <FilterListIcon
                    fontSize="small"
                    sx={{ color: filtersExpanded ? 'primary.main' : 'text.secondary' }}
                  />
                </Badge>
              </IconButton>
            </Box>
          </Box>

          {/* Second Row: Merchant + Descriptor (collapsible) */}
          <Collapse in={filtersExpanded}>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{
                px: 2,
                pb: 2,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <SearchableMultiSelectFilter
                label="Merchant"
                options={merchantOptions}
                value={merchantIds}
                width={200}
                searchPlaceholder="Search merchants…"
                showSelectAllClearAll
                onChange={(values) => { setMerchantIds(values); setPage(0); }}
              />
              <SearchableMultiSelectFilter
                label="Descriptor"
                options={descriptorOptions}
                value={descriptorIds}
                width={200}
                searchPlaceholder="Search descriptors…"
                showSelectAllClearAll
                onChange={(values) => { setDescriptorIds(values); setPage(0); }}
              />
            </Box>
          </Collapse>
        </Box>

        {/* Error State */}
        {(apiError || pageError) && (
          <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            Error: {apiError || pageError}
          </Box>
        )}

        {/* Data Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  {allColumns.filter(c => visibleColumns.includes(c.id)).map(column => (
                    <TableCell key={column.id} sx={{ whiteSpace: 'nowrap' }}>
                      {column.id === 'network_timestamp' ? (
                        <TableSortLabel
                          active={sortBy === 'network_timestamp'}
                          direction={sortBy === 'network_timestamp' ? sortOrder : 'asc'}
                          onClick={() => handleSort('network_timestamp')}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={handleColumnMenuOpen}
                      size="small"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <SettingsOutlinedIcon fontSize="medium" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : alertsData?.alerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No alerts found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  alertsData?.alerts.map((alert) => (
                    <CdrnAlertRow key={alert.verifi_cdrn_alert_id} alert={alert} onResolve={handleResolve} visibleColumns={visibleColumns} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {alertsData && (
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={alertsData.pagination.totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </PageContainer>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 400,
              width: 250,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Configure Columns
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1 }}>
          {allColumns.map((column) => (
            <MenuItem
              key={column.id}
              dense
              sx={{ py: 0.5 }}
              onClick={() => handleColumnToggle(column.id)}
            >
              <Checkbox
                checked={visibleColumns.includes(column.id)}
                size="small"
                sx={{ py: 0, mr: 1 }}
              />
              <Typography variant="body2">{column.label}</Typography>
            </MenuItem>
          ))}
        </Box>
        <Divider />
        <MenuItem onClick={handleResetColumns} dense>
          <Typography variant="body2" color="primary">
            Reset to Default
          </Typography>
        </MenuItem>
      </Menu>
      <ClientCsvDialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} title="Export CDRN Alerts CSV" downloadUrl="/api/client-alerts/cdrn/csv" />
    </div>
  );
}
